/**
 * Procedural sound design via WebAudio — no asset files, works offline.
 * Lightning hum, rocket launch/explosion, rail zap, shotgun, jump pad,
 * item pickup, hit ping, frag chime, death, Q3-style announcer tones.
 */

/**
 * Tables constantes du design sonore, construites UNE fois au chargement du
 * module. Elles étaient réallouées à chaque son joué (un Record de 5 tableaux
 * par annonce, un tableau de notes par ramassage) — plusieurs fois par seconde
 * en plein combat.
 */
const ANNOUNCE_SEQ: Record<'fight' | 'lead' | 'lost' | 'excellent' | 'win', number[]> = {
  fight: [0, 0, 7], lead: [0, 4, 7, 12], lost: [7, 4, 0], excellent: [0, 4, 7, 12, 16], win: [0, 4, 7, 12, 16, 19],
};
const PICKUP_NOTES: Record<'health' | 'armor' | 'ammo' | 'sats' | 'quad', number[]> = {
  sats: [0, 4, 7, 12], quad: [0, 3, 7, 10, 14], health: [0, 5], armor: [0, 5], ammo: [0, 5],
};
const PICKUP_BASE: Record<'health' | 'armor' | 'ammo' | 'sats' | 'quad', number> = {
  sats: 880, quad: 330, armor: 520, health: 440, ammo: 600,
};
const FRAG_NOTES = [0, 7, 12];

export class Sound {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private lgOsc: OscillatorNode | null = null;
  private lgGain: GainNode | null = null;
  private lgNoise: AudioBufferSourceNode | null = null;
  private lgLfo: OscillatorNode | null = null;
  /** buffers de bruit blanc mis en cache par durée (57 600 Math.random() par explosion sinon) */
  private noiseCache = new Map<number, AudioBuffer>();

  private ensure(): AudioContext | null {
    if (this.ctx) { if (this.ctx.state === 'suspended') this.ctx.resume(); return this.ctx; }
    try {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.55;
      this.master.connect(this.ctx.destination);
    } catch { return null; }
    return this.ctx;
  }

  unlock(): void { this.ensure(); }

  private noiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
    const hit = this.noiseCache.get(seconds);
    if (hit) return hit;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.noiseCache.set(seconds, buf);
    return buf;
  }

  private env(ctx: AudioContext, node: AudioNode, a: number, d: number, peak = 1): GainNode {
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(peak, ctx.currentTime + a);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + a + d);
    node.connect(g); g.connect(this.master!);
    return g;
  }

  // ---------------------------------------------------------------- lightning gun (continuous)

  lightningStart(): void {
    const ctx = this.ensure(); if (!ctx || this.lgOsc) return;
    this.lgOsc = ctx.createOscillator();
    this.lgOsc.type = 'sawtooth';
    this.lgOsc.frequency.value = 95;
    const lfo = this.lgLfo = ctx.createOscillator(); lfo.type = 'square'; lfo.frequency.value = 38;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 60;
    lfo.connect(lfoGain); lfoGain.connect(this.lgOsc.frequency);
    const filt = ctx.createBiquadFilter(); filt.type = 'bandpass'; filt.frequency.value = 1800; filt.Q.value = 2;
    this.lgGain = ctx.createGain(); this.lgGain.gain.value = 0.0001;
    this.lgGain.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.03);
    this.lgOsc.connect(filt); filt.connect(this.lgGain); this.lgGain.connect(this.master!);
    // crackle
    this.lgNoise = ctx.createBufferSource(); this.lgNoise.buffer = this.noiseBuffer(ctx, 1); this.lgNoise.loop = true;
    const nf = ctx.createBiquadFilter(); nf.type = 'highpass'; nf.frequency.value = 3000;
    const ng = ctx.createGain(); ng.gain.value = 0.12;
    this.lgNoise.connect(nf); nf.connect(ng); ng.connect(this.lgGain);
    this.lgOsc.start(); lfo.start(); this.lgNoise.start();
  }

  lightningStop(): void {
    const ctx = this.ctx; if (!ctx || !this.lgOsc || !this.lgGain) return;
    this.lgGain.gain.cancelScheduledValues(ctx.currentTime);
    this.lgGain.gain.setValueAtTime(this.lgGain.gain.value, ctx.currentTime);
    this.lgGain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.06);
    // le LFO doit être stoppé lui aussi, sinon un oscillateur fuit à chaque tir
    const o = this.lgOsc, n = this.lgNoise, l = this.lgLfo;
    setTimeout(() => { try { o.stop(); n?.stop(); l?.stop(); } catch {} }, 80);
    this.lgOsc = null; this.lgNoise = null; this.lgGain = null; this.lgLfo = null;
  }

  // ---------------------------------------------------------------- one-shots

  rocketFire(): void {
    const ctx = this.ensure(); if (!ctx) return;
    const n = ctx.createBufferSource(); n.buffer = this.noiseBuffer(ctx, 0.5);
    const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.setValueAtTime(3000, ctx.currentTime); f.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.4);
    n.connect(f); this.env(ctx, f, 0.01, 0.45, 0.8); n.start();
    const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.setValueAtTime(180, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.3);
    this.env(ctx, o, 0.005, 0.3, 0.6); o.start(); o.stop(ctx.currentTime + 0.35);
  }

  explosion(distance = 0): void {
    const ctx = this.ensure(); if (!ctx) return;
    const vol = Math.max(0.15, 1 - distance / 40);
    const n = ctx.createBufferSource(); n.buffer = this.noiseBuffer(ctx, 1.2);
    const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.setValueAtTime(1200, ctx.currentTime); f.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 1.0);
    n.connect(f); this.env(ctx, f, 0.005, 1.1, vol); n.start();
    const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.setValueAtTime(90, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(25, ctx.currentTime + 0.8);
    this.env(ctx, o, 0.005, 0.8, vol * 0.9); o.start(); o.stop(ctx.currentTime + 0.9);
  }

  rail(): void {
    const ctx = this.ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = 'square'; o.frequency.setValueAtTime(1400, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(180, ctx.currentTime + 0.5);
    const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 4000;
    o.connect(f); this.env(ctx, f, 0.005, 0.5, 0.5); o.start(); o.stop(ctx.currentTime + 0.55);
    const o2 = ctx.createOscillator(); o2.type = 'sine'; o2.frequency.setValueAtTime(2400, ctx.currentTime); o2.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.25);
    this.env(ctx, o2, 0.002, 0.25, 0.3); o2.start(); o2.stop(ctx.currentTime + 0.3);
  }

  shotgun(): void {
    const ctx = this.ensure(); if (!ctx) return;
    const n = ctx.createBufferSource(); n.buffer = this.noiseBuffer(ctx, 0.3);
    const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.setValueAtTime(5000, ctx.currentTime); f.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.25);
    n.connect(f); this.env(ctx, f, 0.002, 0.28, 0.9); n.start();
    const o = ctx.createOscillator(); o.type = 'triangle'; o.frequency.setValueAtTime(120, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.15);
    this.env(ctx, o, 0.002, 0.15, 0.7); o.start(); o.stop(ctx.currentTime + 0.2);
  }

  jumpPad(): void {
    const ctx = this.ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.setValueAtTime(220, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.25);
    this.env(ctx, o, 0.01, 0.3, 0.45); o.start(); o.stop(ctx.currentTime + 0.35);
  }

  teleport(): void {
    const ctx = this.ensure(); if (!ctx) return;
    for (let i = 0; i < 3; i++) {
      const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.setValueAtTime(400 + i * 300, ctx.currentTime + i * 0.05); o.frequency.exponentialRampToValueAtTime(1600 + i * 400, ctx.currentTime + 0.3 + i * 0.05);
      this.env(ctx, o, 0.01 + i * 0.05, 0.3, 0.25); o.start(ctx.currentTime + i * 0.05); o.stop(ctx.currentTime + 0.4 + i * 0.05);
    }
  }

  pickup(kind: 'health' | 'armor' | 'ammo' | 'sats' | 'quad'): void {
    const ctx = this.ensure(); if (!ctx) return;
    const base = PICKUP_BASE[kind];
    const notes = PICKUP_NOTES[kind];
    notes.forEach((n, i) => {
      const o = ctx.createOscillator(); o.type = kind === 'sats' ? 'triangle' : 'sine';
      o.frequency.value = base * Math.pow(2, n / 12);
      this.env(ctx, o, 0.005, 0.18, 0.3);
      o.start(ctx.currentTime + i * 0.06); o.stop(ctx.currentTime + i * 0.06 + 0.2);
    });
  }

  hit(): void {
    const ctx = this.ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = 'square'; o.frequency.value = 1800;
    this.env(ctx, o, 0.002, 0.05, 0.18); o.start(); o.stop(ctx.currentTime + 0.06);
  }

  pain(): void {
    const ctx = this.ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = 'sawtooth'; o.frequency.setValueAtTime(300, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(120, ctx.currentTime + 0.2);
    const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 900;
    o.connect(f); this.env(ctx, f, 0.005, 0.2, 0.4); o.start(); o.stop(ctx.currentTime + 0.25);
  }

  frag(): void {
    const ctx = this.ensure(); if (!ctx) return;
    FRAG_NOTES.forEach((n, i) => {
      const o = ctx.createOscillator(); o.type = 'triangle'; o.frequency.value = 523 * Math.pow(2, n / 12);
      this.env(ctx, o, 0.005, 0.25, 0.35); o.start(ctx.currentTime + i * 0.08); o.stop(ctx.currentTime + i * 0.08 + 0.3);
    });
  }

  death(): void {
    const ctx = this.ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = 'sawtooth'; o.frequency.setValueAtTime(260, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.9);
    const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.setValueAtTime(2000, ctx.currentTime); f.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.9);
    o.connect(f); this.env(ctx, f, 0.01, 0.9, 0.5); o.start(); o.stop(ctx.currentTime + 1);
  }

  announce(kind: 'fight' | 'lead' | 'lost' | 'excellent' | 'win'): void {
    const ctx = this.ensure(); if (!ctx) return;
    ANNOUNCE_SEQ[kind].forEach((n, i) => {
      const o = ctx.createOscillator(); o.type = 'square'; o.frequency.value = 220 * Math.pow(2, n / 12);
      const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 1500;
      o.connect(f); this.env(ctx, f, 0.01, 0.22, 0.22); o.start(ctx.currentTime + i * 0.12); o.stop(ctx.currentTime + i * 0.12 + 0.25);
    });
  }

  /** Douille éjectée : clic métal très bref (bruit filtré passe-haut). */
  casing(): void {
    const ctx = this.ensure(); if (!ctx) return;
    const n = ctx.createBufferSource(); n.buffer = this.noiseBuffer(ctx, 0.05);
    const f = ctx.createBiquadFilter(); f.type = 'highpass'; f.frequency.value = 4200; f.Q.value = 1.4;
    n.connect(f); this.env(ctx, f, 0.001, 0.05, 0.09); n.start();
    const o = ctx.createOscillator(); o.type = 'triangle'; o.frequency.setValueAtTime(2600, ctx.currentTime); o.frequency.exponentialRampToValueAtTime(1500, ctx.currentTime + 0.05);
    this.env(ctx, o, 0.001, 0.05, 0.06); o.start(); o.stop(ctx.currentTime + 0.07);
  }

  /** Montée en charge du railgun (glissando court avant le tir). */
  railCharge(): void {
    const ctx = this.ensure(); if (!ctx) return;
    const o = ctx.createOscillator(); o.type = 'sine';
    o.frequency.setValueAtTime(220, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1500, ctx.currentTime + 0.5);
    const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 1200; f.Q.value = 3;
    o.connect(f); this.env(ctx, f, 0.15, 0.4, 0.14); o.start(); o.stop(ctx.currentTime + 0.6);
  }

  footstep(): void {
    const ctx = this.ensure(); if (!ctx) return;
    const n = ctx.createBufferSource(); n.buffer = this.noiseBuffer(ctx, 0.08);
    const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 500;
    n.connect(f); this.env(ctx, f, 0.002, 0.07, 0.12); n.start();
  }
}